/**
 * Нативная сессия: хранение токенов, обновление пары и оповещение о том, что
 * сессия кончилась.
 *
 * Модуль намеренно листовой — он не импортирует ни стор, ни axios-инстансы.
 * Иначе получается цикл `http → store → entities/auth → http`. Стор
 * подписывается на истечение сессии через `onSessionExpired`.
 *
 * Почему токены в localStorage. Бэк кладёт access ещё и в HttpOnly-куку
 * `doit_jwt`, но на localhost:5173 против dev.modules.gamechanger.kz это
 * кросс-сайт: куку браузер не отправит. Поэтому единственный рабочий способ
 * представиться — заголовок `Authorization: Bearer`, а токен надо где-то
 * держать между перезагрузками страницы.
 *
 * Кука v2 до 09.2026 называлась `jwt_token` — так же, как кука старой сессии
 * Bitrix24, и они мешали друг другу. Теперь бэк берёт токен в порядке
 * `?token=` (только WebSocket) → заголовок → кука, а при Bearer куку не
 * смотрит вовсе. Рекомендация бэка — слать Bearer и не опираться на куку.
 *
 * TTL со стороны бэка: access 15 минут, refresh 30 дней. Refresh
 * ротируется — старый инвалидируется при обмене, а повторное использование
 * уже обменянного отзывает всю ветку токенов сессии. Поэтому обновление
 * строго single-flight: параллельные 401 ждут один общий запрос.
 */
import { env } from '@/shared/config';
import { readCookie } from '@/shared/lib/readCookie';

const ACCESS_KEY = 'doit.auth.access-token';
const REFRESH_KEY = 'doit.auth.refresh-token';

/* ------------------------------------------------------------------ URL --- */

/**
 * Склеивает базу и префикс. Если хост пуст (same-origin сборка на проде),
 * ведущий слэш обязателен: без него axios резолвит путь относительно текущего
 * pathname и запросы уезжают не туда.
 */
function joinUrl(host: string, ...parts: string[]): string {
  const tail = parts
    .filter(Boolean)
    .map((part) => part.replace(/^\/+|\/+$/g, ''))
    .join('/');

  if (!host) {
    return tail ? `/${tail}` : '';
  }

  const cleanHost = host.replace(/\/+$/, '');

  return tail ? `${cleanHost}/${tail}` : cleanHost;
}

/** База основного бинаря: `/v2/tasks*`, `/v2/groups*` и legacy-маршруты. */
export const API_BASE_URL = joinUrl(env.apiBase, env.apiPrefix);

/**
 * База core-бинаря: `/auth/*` и `/v2/users*`.
 *
 * Фолбэк парный. Если своей базы у core нет, значит отдельного бинаря на этом
 * стенде тоже нет — берём и хост, и префикс основного API. Брать только хост
 * нельзя: получится голый домен, и `/auth/refresh` уедет в корень.
 */
export const CORE_BASE_URL = env.coreApiBase
  ? joinUrl(env.coreApiBase, env.coreApiPrefix)
  : joinUrl(env.apiBase, env.coreApiPrefix || env.apiPrefix);

/* --------------------------------------------------------------- tokens --- */

let accessToken: string | null = null;
let refreshToken: string | null = null;
let loaded = false;

function load() {
  if (loaded) {
    return;
  }
  loaded = true;
  try {
    accessToken = localStorage.getItem(ACCESS_KEY);
    refreshToken = localStorage.getItem(REFRESH_KEY);
  } catch {
    // Приватный режим / заблокированное хранилище — работаем в памяти.
  }
}

export function getAccessToken(): string | null {
  load();
  return accessToken;
}

export function getRefreshToken(): string | null {
  load();
  return refreshToken;
}

export function setTokens(access: string, refresh: string) {
  loaded = true;
  accessToken = access;
  refreshToken = refresh;
  try {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  } catch {
    // см. load()
  }
}

export function clearTokens() {
  loaded = true;
  accessToken = null;
  refreshToken = null;
  try {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    // см. load()
  }
}

/**
 * Страницы, на которых 401 — это часть нормальной работы («неверный пароль»),
 * а не протухшая сессия. Разлогинивать и редиректить с них нельзя.
 */
const PUBLIC_PATHS = ['/login', '/register', '/auth/google/callback'];

export function isPublicAuthPage(): boolean {
  return PUBLIC_PATHS.includes(window.location.pathname);
}

/** Заголовок авторизации, если токен есть. */
export function authHeader(): { Authorization: string } | undefined {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : undefined;
}

/**
 * Токен для `?token=` у WebSocket: заголовок браузерному сокету не выставить.
 *
 * У бэка v2 `?token=` — первый источник личности, раньше заголовка и куки,
 * поэтому чужой токен здесь ломает сокет, даже когда кука правильная. Под
 * нативной сессией отдаём только её access-токен. Куку самой v2 из JS не
 * прочитать — она HttpOnly (`doit_jwt`), — а `jwt_token`, которую видит
 * `document.cookie`, это старая сессия Bitrix24. Её отдаём, лишь когда
 * нативной сессии нет: то же правило, по которому `http` шлёт Bearer.
 */
export function socketToken(): string | null {
  return getAccessToken() ?? readCookie('jwt_token');
}

/* -------------------------------------------------------------- expired --- */

type SessionExpiredHandler = () => void;

let sessionExpiredHandler: SessionExpiredHandler | null = null;

/** Стор регистрирует сюда свой logout — без импорта стора в сетевой слой. */
export function onSessionExpired(handler: SessionExpiredHandler) {
  sessionExpiredHandler = handler;
}

/** Токенов больше нет и обновить их не удалось: разлогиниваем приложение. */
export function expireSession() {
  clearTokens();
  sessionExpiredHandler?.();
}

/* -------------------------------------------------------------- refresh --- */

let inflightRefresh: Promise<string | null> | null = null;

/**
 * Обновляет пару токенов. Single-flight: сколько бы запросов ни получили 401
 * одновременно, обмен уходит один — иначе второй уйдёт с уже обменянным
 * refresh, бэк посчитает это переиспользованием и отзовёт всю ветку.
 *
 * Возвращает новый access-токен или `null`, если сессия кончилась.
 */
export function refreshSession(): Promise<string | null> {
  if (inflightRefresh) {
    return inflightRefresh;
  }

  const token = getRefreshToken();
  if (!token) {
    return Promise.resolve(null);
  }

  inflightRefresh = (async () => {
    try {
      // Голый fetch, а не наши axios-инстансы: у тех на 401 висит перехватчик,
      // который снова позвал бы обновление — получилась бы рекурсия.
      const res = await fetch(`${CORE_BASE_URL}/auth/refresh`, {
        method: 'POST',
        // Как у остальных запросов к API: бэк обновляет и куку doit_jwt, а
        // без credentials браузер её не примет.
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: token }),
      });

      if (!res.ok) {
        expireSession();
        return null;
      }

      const data = (await res.json()) as {
        access_token?: string;
        refresh_token?: string;
      };

      if (!data.access_token || !data.refresh_token) {
        expireSession();
        return null;
      }

      setTokens(data.access_token, data.refresh_token);
      return data.access_token;
    } catch {
      // Сеть отвалилась — сессию не трогаем, пусть запрос упадёт как есть.
      return null;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}
